import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import {
  ArticleTimeUnitKind,
  ArticleTimeUnitKindToString,
  ArticleUnit,
  AdjustmentKind,
  LeaseAgreementCategory,
} from '../../model/const/enums';
import { IndexClause, LeaseRow } from '../../model/lease-row';
import { Article } from '../../model/article';
import { PropertyTaxRentalChangeModel } from '../../model/property-tax-rental-change';
import { getMonthName } from '../../../core/helper/month';
import { IndexValueService } from '../../service/data/index-value.service';
import { getYearAmount, getPeriodAmount } from '../../helper/rent-calculator';
import { applyPropertyTaxAmounts, hydrateLeaseRowCalculatedAmounts, isPersistedLeaseRow } from '../../helper/property-tax.helpers';
import { CurrencyPipe } from '../../../core/pipe/currency.pipe';
import { MonthNumber } from '../../../core/model/const/enums';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { ValueChangeEvent } from '../../../core/component/forms/input-control.base';
import { getTranslation } from '../../../core/helper/translation';
import { LocalDate } from '../../../core/model/local-date';
import { map, switchMap, tap } from 'rxjs/operators';
import { Money } from '../../../core/model/money';
import { Phrase } from '../../../core/model/phrase';
import { LanguageService } from '../../../core/service/language.service';
import { MessageService } from '../../../core/service/message.service';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { InvoiceRowGroupService } from '../../../invoice/service/data/invoice-row-group.service';
import { UserAccountService } from '../../../common/service/user-account.service';
import { AccountPlanDimensionsService } from '../../../accounting/service/data/account-plan-dimensions.service';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';
import { defaultDimensionLabels } from '../../../accounting/model/dimension-labels';
import { IndexClauseSettingsService } from '../../service/data/index-clause-settings.service';
import { IndexClauseSettings, IndexSeries } from '../../model/index-clause-settings';

type IndexClauseSelectionValue = IndexClause | string;

@Component({
  selector: 'app-lease-row-form',
  templateUrl: './lease-row-form.component.html',
  styleUrls: ['./lease-row-form.component.scss'],
  providers: [IndexValueService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LeaseRowFormComponent implements OnDestroy, OnInit {
  constructor(
    private _fb: FormBuilder,
    private _cf: FormControlExFactory,
    private _dataService: IndexValueService,
    private _indexClauseSettingsService: IndexClauseSettingsService,
    private _invoiceRowGroupService: InvoiceRowGroupService,
    private _messageService: MessageService,
    private readonly _accountService: UserAccountService,
    private _languageService: LanguageService,
    private readonly _dimensionService: AccountPlanDimensionsService,
    private readonly _dimensionLabelsService: DimensionLabelsService
  ) {}

  private _row: LeaseRow;
  get row(): LeaseRow {
    return this._row;
  }
  @Input() set row(value: LeaseRow) {
    if (this.articles && value && value.article) {
      let article = this.articles.find(a => a.id === value.article.id);
      if (article) {
        value.article = article;
        if (this.form && this.form.get('article')) {
          this.form.controls['article'].setValue(article);
        }
      }
    }
    this._row = value;
  }
  @Input() leaseAgreementCategory: LeaseAgreementCategory | string;
  @Input() rentalObjectSelection: SelectionItem[];
  @Input() currencyCode: string;
  @Input() cultureName: string;
  @Input() companyId: string;
  @Input() showProject: boolean;
  @Input() propertyTaxRentalChange?: PropertyTaxRentalChangeModel;
  private _articles: Article[];
  @Input() set articles(value: Article[]) {
    this._articles = value;
    if (this.row.article) {
      let selectedItem = value.find(a => a.id === this.row.article.id);
      if (selectedItem) {
        this.row.article = selectedItem;
        if (this.form && this.form.get('article')) {
          this.form.controls['article'].setValue(this.row.article);
        }
      }
    }
  }
  get articles(): Article[] {
    return this._articles;
  }
  @Output() completed = new EventEmitter<boolean>();

  feePeriodSelection: SelectionItem[] = [];
  invoiceRowGroupSelection: SelectionItem[] = [];

  quarterIndexSelection: SelectionItem[] = [
    new SelectionItem(getMonthName(1), MonthNumber.Januari),
    new SelectionItem(getMonthName(4), MonthNumber.April),
    new SelectionItem(getMonthName(7), MonthNumber.Juli),
    new SelectionItem(getMonthName(10), MonthNumber.October),
  ];

  monthIndexSelection: SelectionItem[] = [
    new SelectionItem(getMonthName(1), MonthNumber.Januari),
    new SelectionItem(getMonthName(2), MonthNumber.Februrary),
    new SelectionItem(getMonthName(3), MonthNumber.March),
    new SelectionItem(getMonthName(4), MonthNumber.April),
    new SelectionItem(getMonthName(5), MonthNumber.May),
    new SelectionItem(getMonthName(6), MonthNumber.June),
    new SelectionItem(getMonthName(7), MonthNumber.Juli),
    new SelectionItem(getMonthName(8), MonthNumber.August),
    new SelectionItem(getMonthName(9), MonthNumber.September),
    new SelectionItem(getMonthName(10), MonthNumber.October),
    new SelectionItem(getMonthName(11), MonthNumber.November),
    new SelectionItem(getMonthName(12), MonthNumber.December),
  ];

  monthSelection: SelectionItem[] = [
    new SelectionItem(getMonthName(1), 1),
    new SelectionItem(getMonthName(2), 2),
    new SelectionItem(getMonthName(3), 3),
    new SelectionItem(getMonthName(4), 4),
    new SelectionItem(getMonthName(5), 5),
    new SelectionItem(getMonthName(6), 6),
    new SelectionItem(getMonthName(7), 7),
    new SelectionItem(getMonthName(8), 8),
    new SelectionItem(getMonthName(9), 9),
    new SelectionItem(getMonthName(10), 10),
    new SelectionItem(getMonthName(11), 11),
    new SelectionItem(getMonthName(12), 12),
  ];

  private readonly _defaultIndexClauses: SelectionItem<IndexClauseSelectionValue>[] = [
    new SelectionItem('LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI80_FAST', IndexClause.FastighetsAgarna),
    new SelectionItem('LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI80_PLUS', IndexClause.KPI80Plus),
    new SelectionItem('LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI80_PLUS_QUARTER', IndexClause.KPI80PlusQuarter),
    new SelectionItem('LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI80_KOMMUNFORBUNDET', IndexClause.Kommunforbundet),
    new SelectionItem('LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI80_MINIMUM3UNITS', IndexClause.KPI80Minimum3Units),
    new SelectionItem('LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI2020_FAST', IndexClause.KPI2020FastighetsAgarna),
    new SelectionItem('LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI2020_PLUS', IndexClause.KPI2020Plus),
    new SelectionItem('LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI2020_PLUS_QUARTER', IndexClause.KPI2020PlusQuarter),
  ];

  indexClauses: SelectionItem<IndexClauseSelectionValue>[] = [...this._defaultIndexClauses];
  private _customIndexClauseSettings: IndexClauseSettings[] = [];

  newIndexRentFroms: SelectionItem[] = [
    new SelectionItem('3', 3),
    new SelectionItem('4', 4),
    new SelectionItem('5', 5),
    new SelectionItem('6', 6),
    new SelectionItem('7', 7),
    new SelectionItem('8', 8),
    new SelectionItem('9', 9),
    new SelectionItem('10', 10),
    new SelectionItem('11', 11),
    new SelectionItem('12', 12),
  ];

  chargedMonthsChange(event: ValueChangeEvent) {
    return;
  }

  private _subscription: Subscription = new Subscription();
  articleId: string;
  articleTimeUnit: string = null;
  baseIndexValue: string = null;
  currentIndexValue: string = null;
  form: FormGroup;
  showQuantity: boolean;
  isIndexArticle: boolean;
  isQuarterIndex: boolean;
  isYearRentArticle: boolean;
  isFixedPercentage: boolean;
  isFullYear: boolean;
  isMonthPeriod: boolean;
  yearMask: RegExp = /^\d{1,4}/;
  isSubmitDisabled: boolean = false;
  private _pipe = new CurrencyPipe(this._languageService);

  private _getDefaultIndexYear(): number {
    let today = new Date();
    let month = today.getMonth();
    let validYear = today.getFullYear();
    if (month < 7) validYear = today.getFullYear() - 1;

    return validYear;
  }

  isVatArticle: boolean;
  displayInvoiceRowGroup: boolean;

  get vatPerYear(): Money {
    if (!this.isVatArticle) return new Money(this.currencyCode, 0);

    let yearRent = this.form.controls['yearRent'].value;
    let vatPercent = this.row.article.vatPercent;
    return new Money(this.currencyCode, (yearRent.amount * vatPercent) / 100);
  }

  get vatPerPeriod(): Money {
    if (!this.isVatArticle) return new Money(this.currencyCode, 0);

    let amount = this.form.controls['amount'].value;
    let vatPercent = this.row.article.vatPercent;
    return new Money(this.currencyCode, (amount.amount * vatPercent) / 100);
  }

  get grossPerPeriod(): Money {
    return new Money(this.currencyCode, this.form.controls['amount'].value.amount + this.vatPerPeriod.amount);
  }

  get grossPerYear(): Money {
    return new Money(this.currencyCode, this.form.controls['yearRent'].value.amount + this.vatPerYear.amount);
  }

  private _applySelectedArticleDefaults(article: Article, zero: Money): void {
    if (article?.isPropertyTax && this.propertyTaxRentalChange && !this.form.controls['oneTimeFee'].value) {
      const keepExistingPropertyTaxAmounts = this._shouldKeepExistingPropertyTaxAmounts(article);

      this.row.article = article;
      this.row.currencyCode = this.currencyCode;
      this.row.chargedMonths = this.form.controls['chargedMonths'].value ?? this.row.chargedMonths;

      if (keepExistingPropertyTaxAmounts) {
        this.form.controls['amount'].setValue(this.row.amount, { emitEvent: false });
        this.form.controls['yearRent'].setValue(this.row.yearRent, { emitEvent: false });
        this._updateVat();
        return;
      }

      applyPropertyTaxAmounts(this.row, this.propertyTaxRentalChange, this.currencyCode);
      hydrateLeaseRowCalculatedAmounts(this.row, this.currencyCode, true);

      this.form.controls['amount'].setValue(this.row.amount, { emitEvent: false });
      this.form.controls['yearRent'].setValue(this.row.yearRent, { emitEvent: false });
      this._updateVat();
      return;
    }

    this.form.controls['amount'].setValue(article.price ? article.price : zero);
    this.form.controls['amount'].setErrors(null);
    this._calculateAmountYearRent();
  }

  private _shouldKeepExistingPropertyTaxAmounts(article: Article): boolean {
    const selectedArticleId = (article as any)?.id;
    const currentArticleId = (this.row?.article as any)?.id;

    return (
      isPersistedLeaseRow(this.row) &&
      !!selectedArticleId &&
      selectedArticleId === currentArticleId &&
      this._hasMoneyAmount(this.row?.amount) &&
      this._hasMoneyAmount(this.row?.yearRent)
    );
  }

  private _hasMoneyAmount(money: Money | null | undefined): boolean {
    return money?.amount !== null && money?.amount !== undefined;
  }

  private _normalizeId(id: string): string {
    return id ? id.toLowerCase() : id;
  }

  private _isCustomIndexClause(indexClause: IndexClause): boolean {
    return indexClause === IndexClause.KPI2020Custom || indexClause === IndexClause.KPI1980Custom;
  }

  private _toIndexClauseSettingsSelectionItem(item: IndexClauseSettings): SelectionItem<string> | null {
    const rawItem = item as any;
    const caption = item.caption ?? rawItem.Caption;
    const id = item.id ?? rawItem.Id;

    if (!caption || !id) {
      return null;
    }

    return new SelectionItem(caption, this._normalizeId(id));
  }

  private _getIndexClauseSelectionValue(): IndexClauseSelectionValue {
    if (this._isCustomIndexClause(this.row.indexClause) && this.row.indexClauseSettingsId) {
      return this._normalizeId(this.row.indexClauseSettingsId);
    }

    return this.row.indexClause;
  }

  private _getSelectedIndexClause(value: IndexClauseSelectionValue = this.form.controls['indexClause'].value): IndexClause {
    if (typeof value === 'number') {
      return value;
    }

    const indexClauseSetting = this._customIndexClauseSettings.find(item => this._normalizeId(item.id) === this._normalizeId(value));
    if (indexClauseSetting) {
      return indexClauseSetting.indexSeries === IndexSeries.KPI2020 ? IndexClause.KPI2020Custom : IndexClause.KPI1980Custom;
    }

    return this.row.indexClause;
  }

  private _getSelectedIndexClauseSettingsId(value: IndexClauseSelectionValue = this.form.controls['indexClause'].value): string | null {
    return typeof value === 'string' ? this._normalizeId(value) : null;
  }

  private _updateIndexClauseSettingsId(indexClause: IndexClauseSelectionValue): void {
    const indexClauseSettingsIdControl = this.form.controls['indexClauseSettingsId'];
    const indexClauseSettingsId = this._getSelectedIndexClauseSettingsId(indexClause);

    if (indexClauseSettingsIdControl.value !== indexClauseSettingsId) {
      indexClauseSettingsIdControl.setValue(indexClauseSettingsId, { emitEvent: false });
    }
  }

  private _calculateNewIndexRent(): Observable<any> {
    if (!this.row.article) return of();
    let baseRent = this.form.controls['baseIndexAmount'].value as Money;
    if (!baseRent) baseRent = new Money(this.currencyCode, 0);
    baseRent.amount = baseRent.amount ? baseRent.amount : 0;
    let baseIndexYear = this.form.controls['baseIndexYear'].value;
    if (!baseIndexYear || baseIndexYear < 1000 || baseIndexYear > 9999) return of();
    let indexYear = this.form.controls['indexYear'].value;
    if (!indexYear || indexYear < 1000 || indexYear > 9999) return of();
    let indexShare = this.form.controls['indexShare'].value;
    if (!indexShare || indexShare < 0 || indexShare > 100) return of();
    let indexClause = this._getSelectedIndexClause();
    let baseIndexMonth = this.form.controls['baseIndexMonth'].value;
    let currentIndexMonth = this.form.controls['indexMonth'].value;    
    let indexMinYearlyPercent = this.form.controls['indexMinYearlyPercent'].value;
    let indexMaxYearlyPercent = this.form.controls['indexMaxYearlyPercent'].value;
    let indexClauseSettingsId = this._getSelectedIndexClauseSettingsId();
    return this._dataService
      .getNewIndexRent(
        baseRent,
        baseIndexYear,
        baseIndexMonth,
        indexYear,
        currentIndexMonth,
        indexShare,
        indexClause,
        indexMinYearlyPercent,
        indexMaxYearlyPercent,
        indexClauseSettingsId
      )
      .pipe(
        tap(res => {
          let newRent = res as Money;
          this.form.controls['indexAmount'].setValue(res);

          if (newRent.amount !== 0) {
            let indexAddition = new Money(newRent.currencyCode, newRent.amount - baseRent.amount).round(2);
            let newInvoiceText = new Phrase(this.row.article.invoiceTexts, this._languageService).getTranslationWithFallback(
              this.cultureName
            );
            if (indexAddition.amount !== 0) {
              newInvoiceText +=
                ' bashyra ' +
                this._pipe.transform(baseRent.amount, false) +
                ', indextillägg ' +
                this._pipe.transform(indexAddition.amount, false);
            }
            this.form.controls['invoiceText'].setValue(newInvoiceText);
            this._calculateBaseIndexAmountYearRent();
            this._updateVat();
          }
        })
      );
  }

  get getBaseIndexValue(): string {
    if (this.form.controls['baseIndexValue']) return this.form.controls['baseIndexValue'].value;
    else return '';
  }

  get getMontNameOctober(): string {
    let tran = this._languageService.getCurrentTranslations();
    return getTranslation(tran, getMonthName(10));
  }

  get formattedIndexAmount(): string {
    if (this.form.controls['indexAmount'])
      return this._pipe.transform(this.form.controls['indexAmount'].value.amount, false, null, this.currencyCode);
    else return '0';
  }

  get formattedYearRent(): string {
    if (this.form.controls['yearRent'])
      return this._pipe.transform(this.form.controls['yearRent'].value.amount, false, null, this.currencyCode);
    else return '0';
  }

  get fixedPercentageIncreaseStartForm(): FormGroup {
    return this.form.controls['fixedPercentageIncreaseStart'] as FormGroup;
  }

  private _setBaseIndexValue(): Observable<any> {
    let baseIndexYear = this.form.controls['baseIndexYear'].value;
    if (!baseIndexYear || baseIndexYear < 1000 || baseIndexYear > 9999) return of();

    let baseIndexMonth = this.form.controls['baseIndexMonth'].value;
    let indexClause = this._getSelectedIndexClause();

    return this._dataService.getIndexValue(baseIndexYear, baseIndexMonth, indexClause).pipe(
      tap(res => {
        this.form.controls['baseIndexValue'].setValue(res);
      }),
      switchMap(() => this._calculateNewIndexRent())
    );
  }

  private _setCurrentIndexValue(): Observable<any> {
    let indexYear = this.form.controls['indexYear'].value;
    if (!indexYear || indexYear < 1000 || indexYear > 9999) return of();
    let indexMonth = this.form.controls['indexMonth'].value;
    let indexClause = this._getSelectedIndexClause();
    return this._dataService.getIndexValue(indexYear, indexMonth, indexClause).pipe(
      tap(res => {
        this.form.controls['indexValue'].setValue(res);
      }),
      switchMap(() => this._calculateNewIndexRent())
    );
  }

  private _refreshIndexValues(indexClause: IndexClause): Observable<any> {
    const baseIndexYear = this.form.controls['baseIndexYear'].value;
    const baseIndexMonth = this.form.controls['baseIndexMonth'].value;
    const indexYear = this.form.controls['indexYear'].value;
    const indexMonth = this.form.controls['indexMonth'].value;

    if (!baseIndexYear || baseIndexYear < 1000 || baseIndexYear > 9999 || !indexYear || indexYear < 1000 || indexYear > 9999) {
      return of();
    }

    return forkJoin({
      baseIndexValue: this._dataService.getIndexValue(baseIndexYear, baseIndexMonth, indexClause),
      currentIndexValue: this._dataService.getIndexValue(indexYear, indexMonth, indexClause),
    }).pipe(
      tap(({ baseIndexValue, currentIndexValue }) => {
        this.form.controls['baseIndexValue'].setValue(baseIndexValue);
        this.form.controls['indexValue'].setValue(currentIndexValue);
      })
    );
  }

  private _calculateIndexAmountYearRent() {
    if (!this.row.article || !this.row.article.timeUnit) return;

    let indexAmount = this.form.controls['indexAmount'].value;
    if (!indexAmount) indexAmount = new Money(this.currencyCode, 0);
    let yearRent = getYearAmount(indexAmount, ArticleTimeUnitKind.Year, 12);
    this.form.controls['yearRent'].setValue(yearRent.round(2));
  }

  private _calculateBaseIndexAmountYearRent() {
    if (!this.row.article || !this.row.article.timeUnit) return;

    let baseIndexAmount = this.form.controls['baseIndexAmount'].value;
    if (!baseIndexAmount) baseIndexAmount = new Money(this.currencyCode, 0);
    let yearRent = getYearAmount(baseIndexAmount, ArticleTimeUnitKind.Year, 12);
    this.form.controls['baseYearRent'].setValue(yearRent.round(2));
  }

  private _calculatingPeriodRent = false;

  private _calculateAmountYearRent() {
    const isOneTimeFee: boolean = this.form.controls['oneTimeFee'].value;
    if (this._calculatingPeriodRent) return;

    if (!this.row.article || (!isOneTimeFee && !this.row.article.timeUnit)) return;

    let amount = this.form.controls['amount'].value as Money;
    amount.currencyCode = this.currencyCode;
    if (!amount.amount) amount.amount = 0;

    let yearRent = isOneTimeFee
      ? new Money(amount.currencyCode, amount.amount * 12)
      : getYearAmount(amount, <ArticleTimeUnitKind>this.row.article.timeUnit, this.monthsRentPerYear).round(2);

    if (this.row.article.unit) {
      yearRent.amount *= this.form.controls['quantity'].value;
    }
    if (yearRent.equals(this.form.controls['yearRent'].value)) return;

    this.form.controls['yearRent'].setValue(yearRent);
    this._updateVat();
  }

  private _chargedMonthsChanged() {
    this.isFullYear = this.monthsRentPerYear == 12;
    this._calculatePeriodRent();
  }

  private _calculatePeriodRent() {
    if (!this.row.article || !this.row.article.timeUnit) return;

    let amount = this.form.controls['yearRent'].value as Money;
    if (!amount.amount) amount.amount = 0;

    if (this.row.article.isReduction && amount.amount > 0) amount.amount = -amount.amount;

    let periodRent = getPeriodAmount(amount, <ArticleTimeUnitKind>this.row.article.timeUnit, this.monthsRentPerYear).round(2);
    if (this.form.controls['oneTimeFee'].value) periodRent = new Money(amount.currencyCode, amount.amount / 12);

    if (periodRent.equals(this.form.controls['amount'].value)) return;

    if (this.row.article.unit) {
      periodRent.amount = periodRent.amount / this.form.controls['quantity'].value;
    }
    this._calculatingPeriodRent = true;
    this.form.controls['amount'].setValue(periodRent, { emitViewToModelChange: false });
    this._updateVat();
    this._calculatingPeriodRent = false;
  }

  get monthsRentPerYear(): number {
    if (Array.isArray(this.form.controls['chargedMonths'].value)) return this.form.controls['chargedMonths'].value.length;
    return 12;
  }

  private _updateVat() {
    this.form.controls['vat'].setValue(this.vatPerPeriod.round(2));
    this.form.controls['vatPerYear'].setValue(this.vatPerYear.round(2));
    this.form.controls['amountIncVat'].setValue(this.grossPerPeriod.round(2));
    this.form.controls['yearRentIncVat'].setValue(this.grossPerYear.round(2));
  }

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = null;
    }
  }

  ngOnInit() {
    if (this.row.article) {
      this.articleId = this.row.article.id;
      if (this.articleId) this.articleTimeUnit = ArticleTimeUnitKindToString(this.row.article.timeUnit);
      this.showQuantity = this.row.article.unit !== ArticleUnit.None;
      this.isIndexArticle = this.row.article.adjustment === AdjustmentKind.Index;
      this.isFixedPercentage = this.row.article.adjustment === AdjustmentKind.FixedPercentage;
      this.isYearRentArticle =
        this.row.article.adjustment === AdjustmentKind.FixedPercentage || this.row.article.adjustment === AdjustmentKind.Index;
      this.isVatArticle = this.row.article.vatPercent > 0;
      this.isQuarterIndex =
        this.row.article.adjustment === AdjustmentKind.Index &&
        (this.row.indexClause === IndexClause.KPI80PlusQuarter || this.row.indexClause === IndexClause.KPI2020PlusQuarter);
      this.isMonthPeriod = this.row.article.timeUnit === ArticleTimeUnitKind.Month;
    }

    for (let year = -1; year <= 1; year++) {
      for (let month = 1; month <= 12; month++) {
        this.feePeriodSelection.push(this.dateToSelection(new Date(new Date().getFullYear() + year, month - 1, 1)));
      }
    }
    let periodStart: Date = undefined;
    if (this.row.fromDate) {
      const requiredPeriodStart = LocalDate.fromDate(this.row.fromDate).toDate();
      if (this.feePeriodSelection.find(i => i.value.getTime() === requiredPeriodStart.getTime()) === undefined) {
        this.feePeriodSelection.splice(0, 0, this.dateToSelection(requiredPeriodStart));
      }
      periodStart = this.feePeriodSelection.find(i => i.value.getTime() === requiredPeriodStart.getTime()).value;
    }

    this._subscription.add(
      this._invoiceRowGroupService.getList().subscribe(val => {
        this.invoiceRowGroupSelection = val.map(p => new SelectionItem(p.caption, p.id));
        this.invoiceRowGroupSelection.splice(0, 0, new SelectionItem('COMMON.NOBODY', null));
      })
    );

    if (this.companyId)
      this._subscription.add(
        this._dimensionService.getCompanySettings(this.companyId).subscribe(res => {
          this.showProject = res.enableProject;
          this._dimensionLabelsService.setControlCaption(this.form?.controls['project'], res.projectLabel);
        })
      );

    var zero = new Money(this.currencyCode, 0);
    this.form = this._fb.group({
      amount: this._cf.createWithOptions('LEASE_AGREEMENT_ROW.AMOUNT', this.row.amount ? this.row.amount : zero, { required: true }),
      baseIndexAmount: this._cf.create('LEASE_AGREEMENT_ROW.BASE_RENT', this.row.baseIndexAmount),
      baseYearRent: this._cf.create('LEASE_AGREEMENT_ROW.BASE_YEAR_RENT', this.row.baseYearRent),
      baseIndexMonth: this._cf.create('LEASE_AGREEMENT_ROW.BASE_INDEX_MONTH', this.row.baseIndexMonth),
      baseIndexYear: this._cf.create('LEASE_AGREEMENT_ROW.BASE_INDEX_YEAR', this.row.baseIndexYear),
      baseIndexValue: this._cf.create('LEASE_AGREEMENT_ROW.BASE_INDEX_VALUE', this.row.baseIndexValue),
      indexAmount: this._cf.create('LEASE_AGREEMENT_ROW.INDEX_RENT', this.row.indexAmount),
      indexMonth: this._cf.create('LEASE_AGREEMENT_ROW.INDEX_MONTH', this.row.indexMonth),
      indexShare: this._cf.create('LEASE_AGREEMENT_ROW.INDEX_SHARE', this.row.indexShare ? this.row.indexShare : 0),
      indexYear: this._cf.create('LEASE_AGREEMENT_ROW.INDEX_YEAR', this.row.indexYear),
      indexValue: this._cf.create('LEASE_AGREEMENT_ROW.INDEX_VALUE', this.row.indexValue),
      fixedPercentageIncrease: this._cf.create('LEASE_AGREEMENT_ROW.FIXED_PERCENTAGE_INCREASE', this.row.fixedPercentageIncrease),
      fixedPercentageIncreaseStart: this._fb.group({}),
      article: this._cf.createMandatory('LEASE_AGREEMENT_ROW.ARTICLE', this.row.article),
      chargedMonths: this._cf.create('LEASE_AGREEMENT_ROW.CHARGED_MONTHS', this.row.chargedMonths),
      fromDate: this._cf.createMandatory('LEASE_AGREEMENT_ROW.FROM_DATE', this.row.fromDate, 'LEASE_AGREEMENT_ROW.HELP_TEXT.FROM_DATE'),
      toDate: this._cf.createWithOptions('LEASE_AGREEMENT_ROW.TO_DATE', this.row.toDate, {
        validator: control => this._validateToDate(control),
      }),

      invoiceText: this._cf.createWithOptions('LEASE_AGREEMENT_ROW.INVOICE_TEXT', this.row.invoiceText, { required: true, length: 200 }),
      note: this._cf.createMaxLength('COMMON.NOTE', this.row.note, 1000),
      project: this._cf.createMaxLength(defaultDimensionLabels.projectLabel, this.row.project, 10),
      quantity: this._cf.create('LEASE_AGREEMENT_ROW.QUANTITY', this.row.quantity),
      rentalObjectId: this._cf.create(
        'LEASE_AGREEMENT_ROW.RENTAL_OBJECT',
        this.row.rentalObjectId,
        null,
        null,
        'LEASE_AGREEMENT_ROW.HELP_TEXT.RENTAL_OBJECT'
      ),
      yearRent: this._cf.create('LEASE_AGREEMENT_ROW.YEAR_RENT', this.row.yearRent),
      vat: this._cf.createReadonly('LEASE_AGREEMENT_ROW.VAT_PER_PERIOD', 0),
      vatPerYear: this._cf.createReadonly('LEASE_AGREEMENT_ROW.VAT_PER_YEAR', 0),
      amountIncVat: this._cf.createReadonly('LEASE_AGREEMENT_ROW.GROSS_PER_PERIOD', 0),
      yearRentIncVat: this._cf.createReadonly('LEASE_AGREEMENT_ROW.GROSS_PER_YEAR', 0),
      rentalObjectSize: this._cf.createHidden(this.row.rentalObjectSize),
      leaseAgreementInvoicePeriod: this._cf.createHidden(this.row.leaseAgreementInvoicePeriod),
      oneTimeFee: this._cf.createWithOptions('LEASE_AGREEMENT_ROW.ONE_TIME_FEE', this.row.oneTimeFee, {
        isReadonly: this.row.hasBeenInvoiced,
      }),
      period: this._cf.createWithOptions('LEASE_AGREEMENT_ROW.ONE_TIME_FEE_PERIOD', periodStart, { isReadonly: this.row.hasBeenInvoiced }),
      indexClause: this._cf.create(
        'LEASE_AGREEMENT_ROW.INDEX_CLAUSE',
        this._getIndexClauseSelectionValue(),
        null,
        null,
        'LEASE_AGREEMENT_ROW.HELP_TEXT.INDEX_CLAUSE'
      ),
      newIndexRentFrom: this._cf.create(
        'LEASE_AGREEMENT_ROW.NEW_INDEX_RENT_FROM',
        this.row.newIndexRentFrom,
        null,
        null,
        'LEASE_AGREEMENT_ROW.HELP_TEXT.NEW_INDEX_RENT_FROM'
      ),
      indexMinYearlyPercent: this._cf.create(
        'LEASE_AGREEMENT_ROW.INDEX_MIN_YEARLY_PRCNT',
        this.row.indexMinYearlyPercent,
        null,
        null,
        'LEASE_AGREEMENT_ROW.HELP_TEXT.INDEX_MIN_YEARLY_PRCNT'
      ),
      indexMaxYearlyPercent: this._cf.create(
        'LEASE_AGREEMENT_ROW.INDEX_MAX_YEARLY_PRCNT',
        this.row.indexMaxYearlyPercent,
        null,
        null,
        'LEASE_AGREEMENT_ROW.HELP_TEXT.INDEX_MAX_YEARLY_PRCNT'
      ),
      voided: this._cf.create('LEASE_AGREEMENT_ROW.VOIDED', this.row.voided),
      invoiceRowGroupId: this._cf.create(
        'INVOICE_ROW_GROUP.INVOICE_ROW_GROUP',
        this.row.invoiceRowGroupId,
        null,
        null,
        'INVOICE_ROW_GROUP.HELP_TEXT.INVOICE_ROW_GROUP'
      ),
      indexClauseSettingsId: this._cf.create(
        'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_SETTINGS_ID',
        this._isCustomIndexClause(this.row.indexClause) ? this._normalizeId(this.row.indexClauseSettingsId) : null,
        null,
        null,
        ''
      ),
    });

    this._updateIndexClauseSettingsId(this.form.controls['indexClause'].value);

    this._subscription.add(
      this._indexClauseSettingsService.getList().subscribe(items => {
        this._customIndexClauseSettings = items;
        this.indexClauses = [
          ...this._defaultIndexClauses,
          ...items
            .map(item => this._toIndexClauseSettingsSelectionItem(item))
            .filter((item): item is SelectionItem<string> => item !== null),
        ];
        this._updateIndexClauseSettingsId(this.form.controls['indexClause'].value);
      })
    );

    this._subscription.add(
      this._accountService.current.subscribe({
        next: () => {
          const profileSettings = this._accountService.profileSettings;
          if (profileSettings.useInvoiceRowGroup) {
            this.displayInvoiceRowGroup = true;
          }
        },
      })
    );

    this._subscription.add(this.form.controls['amount'].valueChanges.subscribe(() => this._calculateAmountYearRent()));
    this._subscription.add(this.form.controls['oneTimeFee'].valueChanges.subscribe(() => this._calculateAmountYearRent()));
    this._subscription.add(
      this.form.controls['baseIndexAmount'].valueChanges
        .pipe(
          switchMap(val => {
            return this._calculateNewIndexRent().pipe(map(res => val));
          })
        )
        .subscribe()
    );
    this._subscription.add(
      this.form.controls['indexClause'].valueChanges
        .pipe(
          tap(value => {
            this._updateIndexClauseSettingsId(value);
            const indexClause = this._getSelectedIndexClause(value);
            this.isQuarterIndex = indexClause === IndexClause.KPI80PlusQuarter || indexClause === IndexClause.KPI2020PlusQuarter;
            this.form.controls['baseIndexMonth'].setValue(MonthNumber.October, { emitEvent: false });
            this.form.controls['indexMonth'].setValue(MonthNumber.October, { emitEvent: false });
            this.form.controls['newIndexRentFrom'].setValue(3);
          }),
          switchMap(value => {
            return this._refreshIndexValues(this._getSelectedIndexClause(value)).pipe(
              switchMap(() => this._calculateNewIndexRent())
            );
          })
        )
        .subscribe()
    );
    this._subscription.add(
      this.form.controls['indexMinYearlyPercent'].valueChanges
        .pipe(
          switchMap(val => {
            return this._calculateNewIndexRent().pipe(map(res => val));
          })
        )
        .subscribe()
    );
    this._subscription.add(
      this.form.controls['indexMaxYearlyPercent'].valueChanges
        .pipe(
          switchMap(val => {
            return this._calculateNewIndexRent().pipe(map(res => val));
          })
        )
        .subscribe()
    );
    this._subscription.add(
      this.form.controls['indexClauseSettingsId'].valueChanges
        .pipe(
          switchMap(val => this._calculateNewIndexRent().pipe(map(res => val)))
        )
        .subscribe()
    );
    this._subscription.add(this.form.controls['indexAmount'].valueChanges.subscribe(() => this._calculateIndexAmountYearRent()));
    this._subscription.add(
      this.form.controls['baseIndexYear'].valueChanges
        .pipe(
          switchMap(val => {
            return this._setBaseIndexValue();
          })
        )
        .subscribe()
    );
    this._subscription.add(
      this.form.controls['baseIndexMonth'].valueChanges
        .pipe(
          switchMap(val => {
            return this._setBaseIndexValue();
          })
        )
        .subscribe()
    );
    this._subscription.add(
      this.form.controls['indexShare'].valueChanges
        .pipe(
          switchMap(val => {
            return this._calculateNewIndexRent().pipe(map(res => val));
          })
        )
        .subscribe()
    );
    this._subscription.add(
      this.form.controls['indexYear'].valueChanges
        .pipe(
          switchMap(val => {
            return this._setCurrentIndexValue().pipe(map(res => val));
          })
        )
        .subscribe()
    );
    this._subscription.add(
      this.form.controls['indexMonth'].valueChanges
        .pipe(
          switchMap(val => {
            return this._setCurrentIndexValue().pipe(map(res => val));
          })
        )
        .subscribe()
    );
    this.isFullYear = this.monthsRentPerYear == 12;
    this._subscription.add(
      this.form.controls['article'].valueChanges.subscribe(value => {
        if (value instanceof Article) {
          if (value.id === this.articleId) {
            return;
          }
          this.articleId = value.id;
          this.articleTimeUnit = value.timeUnitCaption;
          this.row.article = value;
          this.form.controls['invoiceText'].setValue(
            new Phrase(value.invoiceTexts, this._languageService).getTranslationWithFallback(this.cultureName)
          );
          this.showQuantity = value.unit && value.unit > 0;
          this.isVatArticle = value.vatPercent > 0;
          this.isIndexArticle = value.adjustment === AdjustmentKind.Index;
          if (this.isIndexArticle) {
            const defaultIndexClause = IndexClause.FastighetsAgarna;
            this.form.controls['indexClause'].setValue(defaultIndexClause);
            this.form.controls['baseIndexAmount'].setValue(value.price);
            this.form.controls['baseIndexYear'].setValue(this._getDefaultIndexYear());
            this.form.controls['baseIndexMonth'].setValue(MonthNumber.October);
            this.form.controls['indexYear'].setValue(this._getDefaultIndexYear());
            this.form.controls['indexMonth'].setValue(MonthNumber.October);
            this.form.controls['indexShare'].setValue(100);
            this.form.controls['newIndexRentFrom'].setValue(3);
            this._subscription.add(
              this._dataService
                .getIndexValue(this._getDefaultIndexYear(), MonthNumber.October, defaultIndexClause)
                .subscribe(res => {
                  this.form.controls['baseIndexValue'].setValue(res);
                })
            );
            this._subscription.add(
              this._dataService
                .getIndexValue(this._getDefaultIndexYear(), MonthNumber.October, defaultIndexClause)
                .subscribe(res => {
                  this.form.controls['indexValue'].setValue(res);
                })
            );
          }
          this.isFixedPercentage = value.adjustment === AdjustmentKind.FixedPercentage;
          if (this.isFixedPercentage) {
            let defaultFrom = new Date(new Date().getFullYear() + 1, 0, 1);
            this.setDate(this.form.controls['fixedPercentageIncreaseStart'] as FormGroup, defaultFrom);
          }
          this.isYearRentArticle = value.adjustment === AdjustmentKind.FixedPercentage || value.adjustment === AdjustmentKind.Index;
          this.isMonthPeriod = value.timeUnit === ArticleTimeUnitKind.Month;
          this._applySelectedArticleDefaults(value, zero);
        } else {
          this.articleTimeUnit = null;
        }
      })
    );
    this._updateVat();
    this._subscription.add(this.form.controls['quantity'].valueChanges.subscribe(() => this._calculateAmountYearRent()));
    this._subscription.add(
      this.form.controls['fromDate'].valueChanges.subscribe(() => {
        this.form.controls['toDate'].updateValueAndValidity();
        this.form.controls['toDate'].markAllAsTouched();
      })
    );
    this._subscription.add(
      this.form.controls['voided'].valueChanges.subscribe(() => {
        let fromDateValue = LocalDate.fromDate(this.form.controls['fromDate'].value);
        if (fromDateValue == null || !fromDateValue.hasValue()) this.form.controls['fromDate'].setValue(LocalDate.today());
        this.form.controls['toDate'].updateValueAndValidity();
        this.form.controls['toDate'].markAllAsTouched();
      })
    );
    this._subscription.add(this.form.controls['yearRent'].valueChanges.subscribe(() => this._calculatePeriodRent()));
    this._subscription.add(this.form.controls['chargedMonths'].valueChanges.subscribe(() => this._chargedMonthsChanged()));
    this._subscription.add(
      this.form.controls['oneTimeFee'].valueChanges.subscribe(selected => {
        if (selected) {
          const fromDateValue = this.form.controls['fromDate'].value as LocalDate;
          const selection = this.feePeriodSelection.find(
            i => i.value.getMonth() === fromDateValue.month - 1 && i.value.getFullYear() === fromDateValue.year
          );
          this.form.controls['period'].setValue(selection.value);
        }
      })
    );
    this._subscription.add(
      this.form.controls['period'].valueChanges.subscribe(() => {
        const value = this.form.controls['period'].value as Date;
        if (value) {
          const period = LocalDate.fromDate(value);
          this.setDate(this.form.controls['fromDate'] as FormGroup, period.firstDayInMonth());
          this.setDate(this.form.controls['toDate'] as FormGroup, period.lastDayInMonth());
        }
      })
    );
  }

  private _validateToDate(control: AbstractControl): ValidationErrors {
    if (!this.form) return null;

    if (this.form.controls['voided'].value) return null;

    let fromDateValue = LocalDate.fromDate(this.form.controls['fromDate'].value);
    let toDateValue = LocalDate.fromDate(control.value);

    if (toDateValue != null && toDateValue.hasValue() && toDateValue.toDate() < fromDateValue.toDate())
      return { function: { message: 'LEASE_AGREEMENT_ROW.INVALID_PERIOD' } };
    return null;
  }

  get showIndexMinMaxPercent(): boolean {
    const indexClause = this._getSelectedIndexClause();

    return (
      indexClause === IndexClause.FastighetsAgarna ||
      indexClause === IndexClause.KPI80Plus ||
      indexClause === IndexClause.KPI2020FastighetsAgarna ||
      indexClause === IndexClause.KPI2020Plus
    );
  }

  get indexClauseHelp(): string {
    switch (this._getSelectedIndexClause()) {
      case IndexClause.FastighetsAgarna:
        return 'LEASE_AGREEMENT_ROW.HELP_TEXT.INDEX_CLAUSES.KPI80_FAST';
      case IndexClause.KPI80Plus:
        return 'LEASE_AGREEMENT_ROW.HELP_TEXT.INDEX_CLAUSES.KPI80_PLUS';
      case IndexClause.KPI80PlusQuarter:
        return 'LEASE_AGREEMENT_ROW.HELP_TEXT.INDEX_CLAUSES.KPI80_PLUS_QUARTER';
      case IndexClause.Kommunforbundet:
        return 'LEASE_AGREEMENT_ROW.HELP_TEXT.INDEX_CLAUSES.KPI80_KOMMUNFORBUNDET';
      case IndexClause.KPI80Minimum3Units:
        return 'LEASE_AGREEMENT_ROW.HELP_TEXT.INDEX_CLAUSES.KPI80_MINIMUM3UNITS';
      case IndexClause.KPI2020FastighetsAgarna:
        return 'LEASE_AGREEMENT_ROW.HELP_TEXT.INDEX_CLAUSES.KPI2020_FAST';
      case IndexClause.KPI2020Plus:
        return 'LEASE_AGREEMENT_ROW.HELP_TEXT.INDEX_CLAUSES.KPI2020_PLUS';
      case IndexClause.KPI2020PlusQuarter:
        return 'LEASE_AGREEMENT_ROW.HELP_TEXT.INDEX_CLAUSES.KPI2020_PLUS_QUARTER';
      case IndexClause.KPI2020Custom:
        return 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI2020_CUSTOM';
      case IndexClause.KPI1980Custom:
        return 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI2020_CUSTOM';
      default:
        return '';
    }
  }

  private setDate(form: FormGroup, value: Date) {
    if (!form || !form.controls['year']) return;
    form.controls['year'].setValue(value.getFullYear());
    form.controls['month'].setValue(value.getMonth() + 1);
    form.controls['day'].setValue(value.getDate());
  }

  private dateToSelection(date: Date): SelectionItem {
    let tran = this._languageService.getCurrentTranslations();
    return new SelectionItem(date.getFullYear().toString() + ' ' + getTranslation(tran, getMonthName(date.getMonth() + 1)), date);
  }

  cancel() {
    this.completed.emit(false);
  }

  onSubmit() {
    if (!this.row.article || !this.row.article.id) {
      this.form.controls['article'].setErrors({ missing: { message: 'LEASE_AGREEMENT_ROW.MISSING_ARTICLE_ERROR' } });
    }

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    if (
      this.isFixedPercentage &&
      (!this.form.controls['fixedPercentageIncrease'].value || this.form.controls['fixedPercentageIncrease'].value === 0)
    ) {
      this._messageService.showError(
        getTranslation(
          this._languageService.getCurrentTranslations(),
          'LEASE_AGREEMENT_ROW.MISSING_FIXED_PERCENTAGE_ERROR'
        )
      );
      return;
    }

    if (
      this.isIndexArticle &&
      !this.isQuarterIndex &&
      this.form.controls['baseIndexMonth'].value !== this.form.controls['indexMonth'].value
    ) {
      this._messageService.showError(
        getTranslation(
          this._languageService.getCurrentTranslations(),
          'LEASE_AGREEMENT_ROW.NOT_SAME_INDEX_MONTH_ERROR'
        )
      );
      return;
    }

    this.isSubmitDisabled = true;
    if (this.form.pristine) {
      this.completed.emit(false);
    } else {
      Object.assign(this.row, this.form.value);
      this.row.indexClause = this._getSelectedIndexClause();
      this.row.indexClauseSettingsId = this._getSelectedIndexClauseSettingsId();

      if (this.row.oneTimeFee) {
        const value = this.form.controls['period'].value as Date;
        if (value) {
          const period = LocalDate.fromDate(value);
          this.row.fromDate = LocalDate.fromDate(period.firstDayInMonth());
          this.row.toDate = LocalDate.fromDate(period.lastDayInMonth());
        }
      }

      let rowFrom = LocalDate.fromDate(this.row.fromDate).toISOString();
      let rowTo = 'Empty';
      if (this.row.toDate && this.row.toDate.year) rowTo = LocalDate.fromDate(this.row.toDate).toISOString();

      let rowDuePeriodFrom = this.row.currentDuePeriodFrom.toISOString();
      let rowDuePeriodTo = this.row.currentDuePeriodTo.toISOString();

      this._subscription.add(
        this._dataService
          .getCurrentPeriodRent(this.row.yearRent, rowFrom, rowTo, rowDuePeriodFrom, rowDuePeriodTo, this.row.chargedMonths)
          .subscribe(res => {
            this.row.rentThisPeriod = res.periodRent;
            this.row.yearRentInCurrentYear = res.currentYearRent;
            this.completed.emit(true);
          })
      );
    }
  }
}
